import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import TaskCard from './components/TaskCard';
import TaskFilters from './components/TaskFilters';
import NotificationPanel from './components/NotificationPanel';
import UpcomingDeadlines from './components/UpcomingDeadlines';
import TimeLogModal from './components/TimeLogModal';
import TaskDetailsModal from './components/TaskDetailsModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const StaffWorkflowCenter = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isTimeLogModalOpen, setIsTimeLogModalOpen] = useState(false);
  const [isTaskDetailsModalOpen, setIsTaskDetailsModalOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [filters, setFilters] = useState({
    project: 'all',
    priority: 'all',
    status: 'all',
    dueDate: 'all',
    search: ''
  });

  // Mock data
  const mockTasks = [
    {
      id: 1,
      title: "Implement User Authentication Module",
      description: "Develop secure login and registration functionality with JWT tokens and password encryption.",
      project: "Website Redesign",
      priority: "High",
      status: "In Progress",
      assignedBy: "Kadiatou Bah",
      dueDate: "2025-10-15",
      createdDate: "2025-10-08",
      estimatedHours: 16,
      timeLogged: "8.5 hours",
      attachments: [
        { name: "auth_requirements.pdf", size: "2.3 MB" },
        { name: "wireframes.png", size: "1.8 MB" }
      ],
      comments: [
        {
          author: "Kadiatou Bah",
          text: "Please ensure all security best practices are followed.",
          timestamp: "2025-10-09"
        }
      ]
    },
    {
      id: 2,
      title: "Database Migration Script",
      description: "Create migration scripts to transfer legacy data to new database schema.",
      project: "Data Migration",
      priority: "High",
      status: "Pending",
      assignedBy: "Thierno Sow",
      dueDate: "2025-10-12",
      createdDate: "2025-10-07",
      estimatedHours: 12,
      timeLogged: "2 hours",
      attachments: [],
      comments: []
    },
    {
      id: 3,
      title: "Mobile App UI Testing",
      description: "Conduct comprehensive testing of mobile application user interface across different devices.",
      project: "Mobile App Development",
      priority: "Medium",
      status: "Completed",
      assignedBy: "Hawa Bangoura",
      dueDate: "2025-10-10",
      createdDate: "2025-10-05",
      estimatedHours: 8,
      timeLogged: "7.5 hours",
      attachments: [
        { name: "test_results.xlsx", size: "3.1 MB" }
      ],
      comments: [
        {
          author: "Hawa Bangoura",
          text: "Great work on the testing coverage!",
          timestamp: "2025-10-10"
        }
      ]
    },
    {
      id: 4,
      title: "Security Vulnerability Assessment",
      description: "Perform security audit and identify potential vulnerabilities in the current system.",
      project: "Security Audit",
      priority: "High",
      status: "Overdue",
      assignedBy: "Alsény Sylla",
      dueDate: "2025-10-08",
      createdDate: "2025-10-01",
      estimatedHours: 20,
      timeLogged: "15 hours",
      attachments: [],
      comments: []
    },
    {
      id: 5,
      title: "Inventory System Documentation",
      description: "Create comprehensive documentation for the new inventory management system.",
      project: "Inventory System",
      priority: "Low",
      status: "In Progress",
      assignedBy: "Fatoumata Touré",
      dueDate: "2025-10-20",
      createdDate: "2025-10-09",
      estimatedHours: 10,
      timeLogged: "3 hours",
      attachments: [],
      comments: []
    },
    {
      id: 6,
      title: "API Integration Testing",
      description: "Test third-party API integrations and ensure proper error handling.",
      project: "Website Redesign",
      priority: "Medium",
      status: "Pending",
      assignedBy: "Kadiatou Bah",
      dueDate: "2025-10-18",
      createdDate: "2025-10-10",
      estimatedHours: 6,
      timeLogged: null,
      attachments: [],
      comments: []
    }
  ];

  const mockNotifications = [
    {
      id: 1,
      type: "task_assigned",
      title: "New Task Assigned",
      message: "You have been assigned a new task: API Integration Testing",
      timestamp: "2025-10-11T14:30:00Z",
      read: false
    },
    {
      id: 2,
      type: "deadline_reminder",
      title: "Deadline Reminder",
      message: "Database Migration Script is due in 1 day",
      timestamp: "2025-10-11T10:00:00Z",
      read: false
    },
    {
      id: 3,
      type: "status_update",
      title: "Task Completed",
      message: "Mobile App UI Testing has been marked as completed",
      timestamp: "2025-10-10T16:45:00Z",
      read: true
    },
    {
      id: 4,
      type: "announcement",
      title: "Team Meeting",
      message: "Weekly team standup scheduled for tomorrow at 10 AM",
      timestamp: "2025-10-10T09:15:00Z",
      read: true
    },
    {
      id: 5,
      type: "approval_required",
      title: "Approval Required",
      message: "Security Vulnerability Assessment requires manager approval",
      timestamp: "2025-10-09T14:20:00Z",
      read: false
    }
  ];

  const mockUpcomingDeadlines = [
    {
      id: 1,
      title: "Database Migration Script",
      description: "Create migration scripts to transfer legacy data",
      project: "Data Migration",
      dueDate: "2025-10-12"
    },
    {
      id: 2,
      title: "Implement User Authentication Module",
      description: "Develop secure login and registration functionality",
      project: "Website Redesign",
      dueDate: "2025-10-15"
    },
    {
      id: 3,
      title: "API Integration Testing",
      description: "Test third-party API integrations",
      project: "Website Redesign",
      dueDate: "2025-10-18"
    },
    {
      id: 4,
      title: "Inventory System Documentation",
      description: "Create comprehensive documentation",
      project: "Inventory System",
      dueDate: "2025-10-20"
    }
  ];

  useEffect(() => {
    setTasks(mockTasks);
    setFilteredTasks(mockTasks);
    setNotifications(mockNotifications);
    setUpcomingDeadlines(mockUpcomingDeadlines);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, tasks]);

  const applyFilters = () => {
    let filtered = [...tasks];

    // Apply project filter
    if (filters?.project !== 'all') {
      filtered = filtered?.filter(task => 
        task?.project?.toLowerCase()?.includes(filters?.project?.toLowerCase())
      );
    }

    // Apply priority filter
    if (filters?.priority !== 'all') {
      filtered = filtered?.filter(task => 
        task?.priority?.toLowerCase() === filters?.priority?.toLowerCase()
      );
    }

    // Apply status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(task => 
        task?.status?.toLowerCase() === filters?.status?.toLowerCase()
      );
    }

    // Apply due date filter
    if (filters?.dueDate !== 'all') {
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow?.setDate(today?.getDate() + 1);
      const nextWeek = new Date(today);
      nextWeek?.setDate(today?.getDate() + 7);

      filtered = filtered?.filter(task => {
        const dueDate = new Date(task.dueDate);
        switch (filters?.dueDate) {
          case 'today':
            return dueDate?.toDateString() === today?.toDateString();
          case 'this-week':
            return dueDate >= today && dueDate <= nextWeek;
          case 'next-week':
            const weekAfter = new Date(nextWeek);
            weekAfter?.setDate(nextWeek?.getDate() + 7);
            return dueDate > nextWeek && dueDate <= weekAfter;
          case 'overdue':
            return dueDate < today && task?.status !== 'Completed';
          default:
            return true;
        }
      });
    }

    // Apply search filter
    if (filters?.search) {
      filtered = filtered?.filter(task =>
        task?.title?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        task?.description?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        task?.project?.toLowerCase()?.includes(filters?.search?.toLowerCase())
      );
    }

    setFilteredTasks(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      project: 'all',
      priority: 'all',
      status: 'all',
      dueDate: 'all',
      search: ''
    });
  };

  const handleStatusUpdate = (taskId, newStatus) => {
    setTasks(prev => prev?.map(task =>
      task?.id === taskId ? { ...task, status: newStatus } : task
    ));
    
    if (selectedTask && selectedTask?.id === taskId) {
      setSelectedTask(prev => ({ ...prev, status: newStatus }));
    }
  };

  const handleTimeLog = (taskId) => {
    const task = tasks?.find(t => t?.id === taskId);
    setSelectedTask(task);
    setIsTimeLogModalOpen(true);
  };

  const handleViewDetails = (taskId) => {
    const task = tasks?.find(t => t?.id === taskId);
    setSelectedTask(task);
    setIsTaskDetailsModalOpen(true);
  };

  const handleTimeLogSubmit = (timeLogData) => {
    const hours = Math.floor(timeLogData?.duration / 60);
    const minutes = timeLogData?.duration % 60;
    const timeString = `${hours}h ${minutes}m`;
    
    setTasks(prev => prev?.map(task =>
      task?.id === timeLogData?.taskId 
        ? { 
            ...task, 
            timeLogged: task?.timeLogged 
              ? `${task?.timeLogged} + ${timeString}` 
              : timeString 
          }
        : task
    ));
  };

  const handleAddComment = (taskId, commentText) => {
    const newComment = {
      author: "Current User",
      text: commentText,
      timestamp: new Date()?.toISOString()
    };

    setTasks(prev => prev?.map(task =>
      task?.id === taskId 
        ? { 
            ...task, 
            comments: [...(task?.comments || []), newComment] 
          }
        : task
    ));

    if (selectedTask && selectedTask?.id === taskId) {
      setSelectedTask(prev => ({
        ...prev,
        comments: [...(prev?.comments || []), newComment]
      }));
    }
  };

  const handleMarkAsRead = (notificationId) => {
    setNotifications(prev => prev?.map(notification =>
      notification?.id === notificationId 
        ? { ...notification, read: true }
        : notification
    ));
  };

  const getTaskStats = () => {
    const total = tasks?.length;
    const completed = tasks?.filter(task => task?.status === 'Completed')?.length;
    const inProgress = tasks?.filter(task => task?.status === 'In Progress')?.length;
    const overdue = tasks?.filter(task => {
      const dueDate = new Date(task.dueDate);
      const today = new Date();
      return dueDate < today && task?.status !== 'Completed';
    })?.length;

    return { total, completed, inProgress, overdue };
  };

  const stats = getTaskStats();

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <div className={`transition-smooth ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <Header sidebarCollapsed={sidebarCollapsed} />
        
        <main className="pt-16 p-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb />
            
            {/* Page Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-foreground mb-2">Staff Workflow Center</h1>
                <p className="text-muted-foreground">
                  Manage your daily tasks and track work progress
                </p>
              </div>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
              >
                Request New Task
              </Button>
            </div>

            {/* Task Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-card border border-border rounded-lg p-4 card-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Tasks</p>
                    <p className="text-2xl font-bold text-foreground">{stats?.total}</p>
                  </div>
                  <Icon name="CheckSquare" size={24} className="text-primary" />
                </div>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-4 card-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">In Progress</p>
                    <p className="text-2xl font-bold text-foreground">{stats?.inProgress}</p>
                  </div>
                  <Icon name="Clock" size={24} className="text-accent" />
                </div>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-4 card-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Completed</p>
                    <p className="text-2xl font-bold text-foreground">{stats?.completed}</p>
                  </div>
                  <Icon name="CheckCircle" size={24} className="text-success" />
                </div>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-4 card-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Overdue</p>
                    <p className="text-2xl font-bold text-foreground">{stats?.overdue}</p>
                  </div>
                  <Icon name="AlertTriangle" size={24} className="text-error" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-6">
                {/* Task Filters */}
                <TaskFilters
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={handleClearFilters}
                  onSearch={applyFilters}
                />

                {/* Task List */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-foreground">
                      My Tasks ({filteredTasks?.length})
                    </h2>
                  </div>
                  
                  {filteredTasks?.length === 0 ? (
                    <div className="bg-card border border-border rounded-lg p-8 text-center card-shadow">
                      <Icon name="CheckSquare" size={48} className="mx-auto text-muted-foreground mb-4" />
                      <h3 className="text-lg font-medium text-foreground mb-2">No tasks found</h3>
                      <p className="text-muted-foreground">
                        {filters?.search || filters?.project !== 'all' || filters?.priority !== 'all' || filters?.status !== 'all' || filters?.dueDate !== 'all' ?'Try adjusting your filters to see more tasks.' :'You have no tasks assigned at the moment.'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {filteredTasks?.map((task) => (
                        <TaskCard
                          key={task?.id}
                          task={task}
                          onStatusUpdate={handleStatusUpdate}
                          onTimeLog={handleTimeLog}
                          onViewDetails={handleViewDetails}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Notifications */}
                <NotificationPanel
                  notifications={notifications?.slice(0, 5)}
                  onMarkAsRead={handleMarkAsRead}
                  onViewAll={() => console.log('View all notifications')}
                />

                {/* Upcoming Deadlines */}
                <UpcomingDeadlines
                  deadlines={upcomingDeadlines}
                  onViewTask={handleViewDetails}
                />
              </div>
            </div>
          </div>
        </main>
      </div>
      {/* Modals */}
      <TimeLogModal
        isOpen={isTimeLogModalOpen}
        onClose={() => setIsTimeLogModalOpen(false)}
        task={selectedTask}
        onSubmit={handleTimeLogSubmit}
      />
      <TaskDetailsModal
        isOpen={isTaskDetailsModalOpen}
        onClose={() => setIsTaskDetailsModalOpen(false)}
        task={selectedTask}
        onStatusUpdate={handleStatusUpdate}
        onAddComment={handleAddComment}
      />
    </div>
  );
};

export default StaffWorkflowCenter;